import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Search, ExternalLink } from "lucide-react";
import RichText from "../RichText";

const EMPTY = { title: "", slug: "", excerpt: "", body: "", cover_image: "", listing_ids: [], published: true };

export default function AdminArticles({ client }) {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null); // article object being edited (or EMPTY for new)

  const load = async () => {
    try {
      const { data } = await client.get("/admin/articles");
      setArticles(data.items);
    } catch {
      toast.error("No se pudieron cargar los artículos");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (editing) {
    return (
      <ArticleEditor
        client={client}
        article={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await load();
        }}
      />
    );
  }

  return (
    <div data-testid="admin-articles-tab">
      <div className="mb-6 border-b border-border pb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl">Artículos recomendados</h2>
          <p className="text-sm text-muted-foreground mt-1">Se publican en /articulos. Puedes enlazar discos del catálogo a cada artículo.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-1.5 bg-foreground text-background px-4 py-2 vmr-tiny" data-testid="article-new">
          <Plus size={12} /> Nuevo artículo
        </button>
      </div>
      {!articles ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : articles.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aún no hay artículos.</p>
      ) : (
        <div className="border border-border divide-y divide-border">
          {articles.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 p-4">
              <button onClick={() => setEditing(a)} className="flex-1 min-w-0 text-left">
                <span className="block text-sm font-medium truncate">{a.title}</span>
                <span className="block text-xs text-muted-foreground truncate">/articulos/{a.slug} · {a.listing_ids.length} discos enlazados</span>
              </button>
              <span className={`vmr-tiny px-2 py-0.5 border ${a.published ? "border-green-700 text-green-700" : "border-border text-muted-foreground"}`}>
                {a.published ? "Publicado" : "Borrador"}
              </span>
              {a.published && (
                <a href={`/articulos/${a.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Ver en la web">
                  <ExternalLink size={14} />
                </a>
              )}
              <button onClick={() => setEditing(a)} className="vmr-tiny border border-border px-3 py-1.5 hover:border-foreground">Editar</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleEditor({ client, article, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...article, cover_image: article.cover_image || "" });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [linked, setLinked] = useState([]); // listing objects for listing_ids
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Resolve linked ids to records (title + cover) for display.
  useEffect(() => {
    const ids = form.listing_ids;
    if (!ids.length) {
      setLinked([]);
      return;
    }
    client.post("/cart/resolve", { listing_ids: ids })
      .then(({ data }) => {
        const byId = new Map(data.items.map((it) => [it.listing_id, it]));
        setLinked(ids.map((id) => byId.get(id) || { listing_id: id, artist: "No disponible", title: `#${id}` }));
      })
      .catch(() => setLinked(ids.map((id) => ({ listing_id: id, artist: "", title: `#${id}` }))));
  }, [form.listing_ids, client]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await client.get("/listings", { params: { q: term, per_page: 8 } });
        setResults(data.items || []);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, client]);

  const addListing = (id) => {
    if (!form.listing_ids.includes(id)) set("listing_ids")([...form.listing_ids, id]);
    setQ("");
    setResults([]);
  };
  const removeListing = (id) => set("listing_ids")(form.listing_ids.filter((x) => x !== id));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, cover_image: form.cover_image || null };
      if (article.id) await client.put(`/admin/articles/${article.id}`, payload);
      else await client.post("/admin/articles", payload);
      toast.success("Artículo guardado");
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Borrar el artículo "${article.title}"?`)) return;
    try {
      await client.delete(`/admin/articles/${article.id}`);
      toast.success("Artículo borrado");
      await onSaved();
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/admin/upload", fd);
      set("cover_image")(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la imagen");
    }
  };

  const input = "w-full bg-transparent border border-border px-3 py-2 text-sm mt-1 focus:outline-none focus:border-foreground";

  return (
    <div data-testid="article-editor">
      <div className="mb-6 border-b border-border pb-4 flex items-center justify-between">
        <h2 className="text-2xl">{article.id ? "Editar artículo" : "Nuevo artículo"}</h2>
        <button onClick={onClose} className="inline-flex items-center gap-1 vmr-tiny text-muted-foreground hover:text-foreground"><X size={12} /> Volver</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <label className="block">
            <span className="vmr-tiny text-muted-foreground">Título</span>
            <input value={form.title} onChange={(e) => set("title")(e.target.value)} className={input} data-testid="article-title" />
          </label>
          <label className="block">
            <span className="vmr-tiny text-muted-foreground">Resumen (sale en el listado)</span>
            <textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt")(e.target.value)} className={input} />
          </label>
          <div>
            <div className="flex items-center justify-between">
              <span className="vmr-tiny text-muted-foreground">Texto · <code>## Título</code>, <code>- lista</code>, <code>**negrita**</code>, línea en blanco = párrafo</span>
              <button onClick={() => setPreview((p) => !p)} className="vmr-tiny underline">{preview ? "Editar" : "Vista previa"}</button>
            </div>
            {preview ? (
              <div className="border border-border p-4 mt-1 text-sm leading-relaxed min-h-[300px]"><RichText text={form.body} /></div>
            ) : (
              <textarea rows={16} value={form.body} onChange={(e) => set("body")(e.target.value)} className={`${input} font-mono text-[13px]`} data-testid="article-body" />
            )}
          </div>
        </div>

        <div className="space-y-5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => set("published")(e.target.checked)} />
            Publicado (visible en la web)
          </label>
          <label className="block">
            <span className="vmr-tiny text-muted-foreground">Dirección (slug, opcional)</span>
            <input value={form.slug} onChange={(e) => set("slug")(e.target.value)} placeholder="se genera del título" className={input} />
          </label>
          <div>
            <span className="vmr-tiny text-muted-foreground">Imagen de portada (opcional)</span>
            {form.cover_image && <img src={form.cover_image} alt="" className="w-full aspect-[4/3] object-cover mt-1 bg-[hsl(var(--muted))]" />}
            <input value={form.cover_image} onChange={(e) => set("cover_image")(e.target.value)} placeholder="URL o sube una imagen" className={input} />
            <input type="file" accept="image/*" onChange={uploadCover} className="mt-2 text-xs" />
            <p className="text-xs text-muted-foreground mt-1">Si no pones imagen se usa la portada del primer disco enlazado.</p>
          </div>

          <div>
            <span className="vmr-tiny text-muted-foreground">Discos recomendados</span>
            <ul className="mt-1 border border-border divide-y divide-border">
              {linked.length === 0 && <li className="p-2.5 text-xs text-muted-foreground">Ninguno todavía.</li>}
              {linked.map((it) => (
                <li key={it.listing_id} className="flex items-center gap-2 p-2">
                  {it.thumb && <img src={it.thumb} alt="" className="w-8 h-8 object-cover" />}
                  <span className="flex-1 min-w-0 text-xs truncate">{it.artist} — {it.title}</span>
                  <button onClick={() => removeListing(it.listing_id)} aria-label="Quitar" className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
                </li>
              ))}
            </ul>
            <div className="relative mt-2">
              <div className="flex items-center gap-2 border border-border px-2 py-1.5">
                <Search size={12} className="text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar disco para añadir…" className="flex-1 bg-transparent text-sm outline-none" data-testid="article-listing-search" />
              </div>
              {results.length > 0 && (
                <ul className="absolute left-0 right-0 z-10 bg-background border border-border border-t-0 max-h-64 overflow-y-auto">
                  {results.map((it) => (
                    <li key={it.listing_id}>
                      <button onClick={() => addListing(it.listing_id)} className="w-full flex items-center gap-2 p-2 text-left hover:bg-[hsl(var(--muted))]">
                        <img src={it.thumb || "/brand/cover-placeholder.png"} alt="" className="w-8 h-8 object-cover" />
                        <span className="flex-1 min-w-0 text-xs truncate">{it.artist} — {it.title}</span>
                        <Plus size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <button disabled={saving} onClick={save} className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background py-2.5 vmr-tiny disabled:opacity-40" data-testid="article-save">
              <Save size={12} /> Guardar
            </button>
            {article.id && (
              <button onClick={remove} className="w-full inline-flex items-center justify-center gap-2 border border-border py-2 vmr-tiny text-muted-foreground hover:text-foreground">
                <Trash2 size={12} /> Borrar artículo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
